import _ from 'lodash';
import crypto from 'crypto-browserify';
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VoicemailIcon from '@mui/icons-material/Voicemail';
import { useTranslation } from 'react-i18next';
import PhoneIcon from '@mui/icons-material/Phone';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment-timezone';
import { useState } from 'react';
import { selectNewVoicemail, selectSavedVoicemail } from '../../../state/voicemail/voicemailSlice';

import { navStackBackAction } from '../../../sagas/routeStacks/navStackBack/action';
import formatPhoneNumber from '../../../utils/formatPhoneNumber';
import Avatar from '../../molecules/Avatar/Avatar';
import matchContactType from '../../../utils/matchContactType';
import matchContact from '../../../utils/matchContact';
import { selectSMS, selectUser, selectUserTimeZone } from '../../../state/user/userSlice';
import { navStackPushAction } from '../../../sagas/routeStacks/navStackPush/action';
import { getContactDetailsRoute, getContactFormRoute, getContactsRoute } from '../../../routePaths';
import { INFO, WORKFLOW_SELECT_CONTACT, WORKFLOW_VOICEMAIL_FORWARD } from '../../../constants';
import { setSnackbar } from '../../../state/snackbar/snackbarSlice';
import { initiateUACallAction } from '../../../sagas/ua/initiateUACall/action';
import { selectCallFromDevice } from '../../../state/webphoneDevice/webphoneDeviceSlice';
import { saveVoicemailAction } from '../../../sagas/voicemail/saveVoicemail/action';
import { contactsFilteredByAction } from '../../../sagas/contacts/contactsFilteredBy/action';
import { deleteVoicemailAction } from '../../../sagas/voicemail/deleteVoicemail/action';
import { selectChatAllowed } from '../../../state/configs/configsSlice';
import { addCardAction } from '../../../sagas/cardManagment/addCard/action';
import generateSmsObject from '../../../utils/chat/generateSmsObject';
import { selectSessions, setSessionsList } from '../../../state/chat/chatSlice';
import generateChatObjectFromChips from '../../../utils/chat/generateChatObjectFromChips';
import ForwardTag from '../../atoms/ForwardTag/ForwardTag';

function VoicemailDetails() {
  const debug = localStorage.getItem('debugFlag') === 'true';
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // get all voicemails
  const newVoicemail = useSelector(selectNewVoicemail);
  const savedVoicemail = useSelector(selectSavedVoicemail);
  const allVoicemail = [...newVoicemail, ...savedVoicemail];

  // get the id and then get the voicemail from all of them
  const location = useLocation();
  const voicemailIndex = location.pathname.replace('/voicemail-details/', '');
  const voiceMail = allVoicemail.find((voicemail) => voicemail.index === voicemailIndex);

  if (debug) console.debug('this is the voicemail', voiceMail);

  // back button
  const handleBack = () => {
    dispatch(navStackBackAction());
  };

  // caller info
  const getCallerDisplayName = () => {
    const callDetailsName = voiceMail?.callDetails?.name;
    const fromUser = voiceMail?.FromUser;

    if (callDetailsName) {
      return formatPhoneNumber(String(callDetailsName));
    }

    if (fromUser) {
      return formatPhoneNumber(String(fromUser));
    }

    return '';
  };

  // get the contact type
  const contactType = matchContactType(voiceMail.contact, voiceMail.FromUser);
  if (debug) console.debug('this is contactType', contactType);

  const shouldShowCallerNumber = contactType !== 'unknown'
  || (contactType === 'unknown'
    && voiceMail.callDetails?.name
    && formatPhoneNumber(String(voiceMail.FromUser)) !== getCallerDisplayName());

  // voicemail info
  const voicemailIndicatorStyle = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
  };

  const voicemailInfoStyle = {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '13px',
    color: 'rgba(0,0,0,0.54)',
  };

  // get forwarded by and match contact
  const forwardedByContact = voiceMail.VmailForwarded ? matchContact(voiceMail.VmailForwarded) : null;
  if (debug) console.debug('this is forwardedByContact', forwardedByContact);

  // time and duration
  const userTimeZone = useSelector(selectUserTimeZone) || moment.tz.guess(); // Fallback to auto-detect if missing

  const formattedDateTime = moment.utc(voiceMail.NmsRecStartTime) // Start in UTC
    .tz(userTimeZone) // Convert to user's timezone from Redux
    .format(`dddd, MMMM D, YYYY [${t('at')}] h:mm A`);

  // Convert voicemail duration into minutes & seconds
  const duration = moment.duration(voiceMail.NmsRecDuration, 'seconds');
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  let formattedDuration = '';

  if (minutes && seconds) {
    formattedDuration = `${minutes} ${t('MINUTES')} ${seconds} ${t('SECONDS')}`;
  } else if (minutes) {
    formattedDuration = `${minutes} ${t('MINUTES')}`;
  } else {
    formattedDuration = `${seconds} ${t('SECONDS')}`;
  }

  // voicemail actions
  const rowStyle = {
    display: 'flex',
    p: 2,
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
    cursor: 'pointer',
  };
  const rowTextStyle = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    ml: 2.5,
    color: 'rgba(0,0,0,.54)',
    fontWeight: 'bold',
  };
  // const iconButtonStyle = {
  //   p: 0,
  //   pointerEvents: 'none',
  // };

  // Call action
  const callFromDevice = useSelector(selectCallFromDevice);

  const handleCall = (voicemail) => {
    if (debug) console.debug('handleCall', voicemail);
    dispatch(setSnackbar({
      type: INFO,
      open: true,
      message: `${t('CALLING_FROM')} ${callFromDevice.display}`,
    }));
    dispatch(initiateUACallAction({ phoneNumber: voicemail.FromUser, genCard: true }));
  };

  // Send SMS action
  // determine if it's user or phone number
  let messageType = null;
  // get the user's number
  const chatAllowed = useSelector(selectChatAllowed);
  const sms = useSelector(selectSMS);

  const user = useSelector(selectUser);
  const currentSessions = useSelector(selectSessions);
  if (contactType === 'extension') {
    // is chat
    messageType = 'chat';
  } else if (contactType === 'work' || contactType === 'mobile' || contactType === 'home') {
    messageType = 'sms';
  } else if (voiceMail.NmsAni) {
    // is unknown
    messageType = 'sms';
  }

  const contactToChip = (contactToChipParam) => {
    if (debug) console.debug('this is contactToChipParam', contactToChipParam);
    return {
      contact: contactToChipParam,
      key: (voiceMail.contact) ? voiceMail.contact.id : Date.now(),
      id: (voiceMail.contact) ? voiceMail.contact.id : Date.now(),
      contact_id: (voiceMail.contact) ? voiceMail.contact.id : Date.now(),
      label: contactToChipParam.name,
      name: contactToChipParam.name,
      initials: (contactToChipParam.initials !== undefined) ? contactToChipParam.initials : '',
      gravatar: (contactToChipParam.gravatar !== undefined) ? contactToChipParam.gravatar : '',
    };
  };

  const handleClickToChat = () => {
    if (debug) console.debug('handleClickToChat this is user: ', user);
    if (debug) console.debug('handleClickToChat this is voiceMail: ', voiceMail);
    if (debug) console.debug('handleClickToChat this is contactType: ', contactType);

    // remove + sign from cdr.number
    const callerNumber = voiceMail.FromUser || '';
    let sanitizedNumber = String(callerNumber).replace('+', ''); // Ensure it's a string before replacing

    if (sanitizedNumber.length === 10) {
      sanitizedNumber = `1${sanitizedNumber}`;
    }

    if (contactType === 'work' || contactType === 'mobile' || contactType === 'home' || contactType === 'unknown') {
      // sms
      let chipDataConfirm = null;
      if (voiceMail.contact) {
        chipDataConfirm = [contactToChip(voiceMail.contact)];
      } else {
        chipDataConfirm = [contactToChip({
          label: sanitizedNumber,
          name: sanitizedNumber,
          customContact: true,
        })];
      }

      // toHash to have subscriberLogin, cdr.number, sms[0] if set (ex: 1000@cwei1714222035518582907681cwei)
      // order the sanitizedNumber and sms[0].number if set, make sure that sms[0].number is default to empty string, join by empty string
      const orderedNumbers = [sanitizedNumber, (sms[0]?.number || '').replace('+', '')].sort().join('');
      const toHash = `${user.subscriberLogin}${orderedNumbers}${user.domain}`;

      if (debug) console.debug('this is toHash: ', toHash);

      const sessionId = crypto.createHash('md5').update(toHash).digest('hex');

      if (debug) console.debug('this is sessionId: ', sessionId);

      // look up session ID if the session exists
      const sessionInList = _.find(currentSessions, {
        remote: sanitizedNumber,
      });

      if (sessionInList !== undefined) {
        // IF session exists, open that session
        const sendMessageParams = {
          chat: sessionInList,
        };

        dispatch(addCardAction({
          type: 'new-chat',
          sessionId: sessionInList.id,
          sendMessageParams,
        }));
      } else if (sms[0]?.number) {
        // if session does not exist, create new session
        const smsObj = generateSmsObject({
          sessionId,
          chipData: chipDataConfirm,
          smsani: sms[0].number,
        });

        const updatedSessions = [smsObj, ...currentSessions];
        const sendMessageParams = {
          chat: smsObj,
        };

        dispatch(setSessionsList(updatedSessions));
        dispatch(addCardAction({
          type: 'new-chat',
          sessionId: smsObj.id,
          sendMessageParams,
        }));
      }
    } else {
      // make chat card
      const chatObj = generateChatObjectFromChips([contactToChip(voiceMail.contact)]);

      // open card
      const sendMessageParams = {
        chat: chatObj,
      };

      if (debug) console.debug('this is sendMessageParams: ', sendMessageParams);

      // might need to add the conversation to the sessions in state
      const sessionInList = _.find(currentSessions, { id: chatObj.id });

      // if session is not in the current list, then add to the list
      if (sessionInList === undefined) {
        const updatedSessions = [chatObj, ...currentSessions];
        dispatch(setSessionsList(updatedSessions));
      }

      dispatch(addCardAction({
        type: 'new-chat',
        sessionId: chatObj.id,
        sendMessageParams,
      }));
    }
  };

  let messageContent = null;

  if (chatAllowed) {
    if (messageType === 'sms' && sms) {
      messageContent = (
        <Box sx={rowStyle} onClick={handleClickToChat}>
          <IconButton sx={{ p: 0 }}>
            <ChatBubbleIcon />
          </IconButton>
          <Box sx={rowTextStyle}>
            {t('SEND_SMS_ONLY')}
          </Box>
        </Box>
      );
    } else if (messageType === 'chat') {
      messageContent = (
        <Box sx={rowStyle} onClick={handleClickToChat}>
          <IconButton sx={{ p: 0 }}>
            <ChatBubbleIcon />
          </IconButton>
          <Box sx={rowTextStyle}>
            {t('SEND_A_CHAT_MESSAGE')}
          </Box>
        </Box>
      );
    }
  }

  // Save Voicemail Action
  const handleSave = (voicemail) => {
    dispatch(saveVoicemailAction(voicemail));
  };

  // Forward Voicemail Action
  const handleForward = (voicemail) => {
    if (debug) console.debug('handleForward', voicemail);
    dispatch(contactsFilteredByAction('coworkers'));
    dispatch(navStackPushAction({
      route: getContactsRoute(),
      state: { voicemail, workflow: WORKFLOW_VOICEMAIL_FORWARD },
      fromName: null,
      fromState: null,
    }));
  };

  // Create/Navigate to contact
  const handleNavigateToContact = (contactId) => {
    dispatch(navStackPushAction({
      route: getContactDetailsRoute(contactId),
      state: { contactId },
      fromName: null,
      fromState: null,
    }));
  };
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [contactRedirectType, setContactRedirectType] = useState(null);

  const handleCreateClick = (event) => {
    setContactRedirectType('create');
    setAnchorEl(event.currentTarget);
  };

  const handleAddToClick = (event) => {
    setContactRedirectType('update');
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event.currentTarget.id.startsWith('ph')) {
      const phoneType = event.currentTarget.id.split('-')[1]; // retrieve type from element id
      if (contactRedirectType === 'create') {
        const contactFormDetails = {
          add: true,
          type: 'add',
          editWork: phoneType === 'work' ? voiceMail.NmsAni : '',
          editMobile: phoneType === 'mobile' ? voiceMail.NmsAni : '',
          editHome: phoneType === 'home' ? voiceMail.NmsAni : '',
        };
        dispatch(navStackPushAction({
          route: getContactFormRoute(),
          state: contactFormDetails,
          fromName: null,
          fromState: null,
        }));
      } else {
        navigate(getContactsRoute(), {
          state: { phoneType, number: voiceMail.NmsAni, workflow: WORKFLOW_SELECT_CONTACT },
        });
      }
    }
    setAnchorEl(null);
  };

  // Delete Voicemail Action
  const handleDelete = (voicemail) => {
    if (debug) console.debug('handleDelete', voicemail);
    dispatch(deleteVoicemailAction(voicemail));
    dispatch(navStackBackAction());
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#ffffff',
      }}
    >
      {/* back button */}
      <Box
        // header
        sx={{
          p: 1,
          display: 'flex',
        }}
      >
        <Box
          sx={{
            flexGrow: 11,
          }}
        >
          <IconButton
            onClick={handleBack}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
      </Box>

      {/* caller info */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
        }}
      >
        {/* Avatar on the left */}
        <Avatar
          size="medium-small"
          style={{
            alignSelf: 'center',
          }}
          initials={voiceMail.contact?.initials || null}
          gravatar={voiceMail.contact?.gravatar}
        />

        {/* Text container */}
        <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center', // Ensures vertical alignment
            }}
          >
            {/* Primary: {contact name} > {caller ID} > {caller number} */}
            <Typography
              noWrap
              sx={{
                display: 'flex', // Ensures inline layout
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {/* FWD'd tag for voicemail if it was forwarded */}
              {voiceMail.VmailForwarded && <ForwardTag />}
              {getCallerDisplayName()}
            </Typography>
          </Box>

          {/* Additional Text Below */}
          {/* Secondary: {contact number type} • {caller number} > {“Unknown”} */}
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {contactType === 'unknown'
                ? 'Unknown'
                : t({
                  mobile: 'MOBILE',
                  work: 'WORK',
                  home: 'HOME',
                  extension: 'EXTENSION',
                }[contactType] || 'UNKNOWN')}
            </Typography>

            {shouldShowCallerNumber && (
            <Typography variant="body2" color="text.secondary">
              •
              {' '}
              {formatPhoneNumber(String(voiceMail.FromUser))}
            </Typography>
            )}
          </Box>

        </Box>
      </Box>

      {/* divider line */}
      <Divider />

      {/* Voicemail Info */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column', // Stack elements vertically
          p: 2,
        }}
      >
        <Box sx={voicemailIndicatorStyle}>
          <VoicemailIcon
            sx={{
              mr: 1,
              fontSize: '18px',
              opacity: 0.54,
            }}
          />
          <Box sx={{ fontWeight: 'bold' }}>
            {t('VOICEMAIL_RECEIVED')}
          </Box>
        </Box>
        {/* only if forwarded */}
        {voiceMail.VmailForwarded && forwardedByContact && (
        <Box sx={{ ...voicemailInfoStyle, display: 'inline-flex' }}>
          <Box component="span">
            {t('FORWARDED_VIA')}
            {' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {forwardedByContact.extension_name}
              {' '}
              (
              {forwardedByContact.extension}
              )
            </Box>
          </Box>
        </Box>
        )}
        <Box sx={voicemailInfoStyle}>
          {formattedDateTime}
        </Box>

        <Box sx={voicemailInfoStyle}>
          {formattedDuration}
        </Box>
      </Box>

      <Divider />

      {/* voicemail actions */}
      {/* Call action */}
      <Box sx={rowStyle} onClick={() => handleCall(voiceMail)}>
        <IconButton sx={{ p: 0 }}>
          <PhoneIcon />
        </IconButton>
        <Box sx={rowTextStyle}>
          {t('CALL')}
          {' '}
          {getCallerDisplayName()}
        </Box>
      </Box>

      <Divider />

      {/* Send Message action */}
      {messageContent}

      <Divider />
      <Box sx={rowStyle} onClick={() => handleSave(voiceMail)}>
        <IconButton sx={{ p: 0 }}>
          <SaveIcon />
        </IconButton>
        <Box sx={rowTextStyle}>
          {t('SAVE_VOICEMAIL')}
        </Box>
      </Box>

      <Divider />
      <Box sx={rowStyle} onClick={() => handleForward(voiceMail)}>
        <IconButton sx={{ p: 0 }}>
          <ArrowForwardIcon />
        </IconButton>
        <Box sx={rowTextStyle}>
          {t('FORWARD_VOICEMAIL')}
        </Box>
      </Box>

      {/* TODO contact options */}
      <Divider />
      {voiceMail.contact && (
        <Box
          sx={rowStyle}
          onClick={() => handleNavigateToContact(voiceMail.contact.id)}
        >
          <IconButton sx={{ p: 0 }}>
            <PersonIcon />
          </IconButton>
          <Box sx={rowTextStyle}>
            {t('VIEW_CONTACT_INFO')}
          </Box>
        </Box>
      )}

      {!voiceMail.contact && (
        <Box>
          <Box
            sx={rowStyle}
            onClick={handleCreateClick}
          >
            <IconButton sx={{ p: 0 }}>
              <PersonAddIcon />
            </IconButton>
            <Box sx={rowTextStyle}>
              {t('CREATE_NEW_CONTACT')}
            </Box>
          </Box>
          <Divider />
          <Box
            sx={rowStyle}
            onClick={handleAddToClick}
          >
            <IconButton sx={{ p: 0 }}>
              <PersonIcon />
            </IconButton>
            <Box sx={rowTextStyle}>
              {t('ADD_TO_EXISTING_CONTACT')}
            </Box>
          </Box>
          <Menu
            id="type-select-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem
              disabled
              sx={{ color: 'rgba(0,0,0,0.54)', opacity: '1.0 !important' }}
            >
              {`${t('SELECT_TYPE')}:`}
            </MenuItem>
            <Divider />
            <MenuItem
              id="ph-work"
              onClick={handleClose}
            >
              {t('WORK')}
            </MenuItem>
            <MenuItem
              id="ph-mobile"
              onClick={handleClose}
            >
              {t('MOBILE')}
            </MenuItem>
            <MenuItem
              id="ph-home"
              onClick={handleClose}
            >
              {t('HOME')}
            </MenuItem>
          </Menu>
        </Box>
      )}

      <Divider />
      <Box sx={rowStyle} onClick={() => handleDelete(voiceMail)}>
        <IconButton sx={{ p: 0 }}>
          <DeleteIcon />
        </IconButton>
        <Box sx={rowTextStyle}>
          {t('DELETE_VOICEMAIL')}
        </Box>
      </Box>

    </Box>
  );
}

export default VoicemailDetails;
